/**
 * サブスクリプション解約パネル（v2.1対応）
 * @module components/subscription/CancellationPanel
 */

import { useState, useMemo } from 'react';
import {
  Card,
  Space,
  Typography,
  Alert,
  Radio,
  Input,
  Checkbox,
  Button,
  App,
  Collapse,
  Timeline,
} from 'antd';
import { ExclamationCircleOutlined, HistoryOutlined } from '@ant-design/icons';
import { format } from 'date-fns';
import { ja } from 'date-fns/locale';
import type { Company } from '../../types/company';
import { trpc } from '../../lib/trpc';
import {
  useCancelSubscription,
  useUndoCancellation,
  useCanCancelSubscription,
  useLatestCancellationRequest,
  useCancellationHistory,
  type CompanyActiveState,
} from '../../hooks/useCompanyState';
import {
  CANCELLATION_REASON_LABELS,
  CANCELLATION_REQUEST_STATUS_LABELS,
  type CancellationReason,
} from '@shared/types/cancellation';
import { CancellationConfirmDialog } from './CancellationConfirmDialog';
import { UndoCancellationConfirmDialog } from './UndoCancellationConfirmDialog';
import { ReactivationPreviewDialog } from './ReactivationPreviewDialog';

const { Title, Text, Paragraph } = Typography;

interface CancellationPanelProps {
  company: Company;
  companyState: {
    activeState?: CompanyActiveState;
    subscriptionStatus?: string;
    isActive?: boolean;
  };
  onUpdate: () => void;
}

/**
 * 日付フォーマット
 */
const formatDate = (date: Date | string | null | undefined): string => {
  if (!date) return '-';
  return format(new Date(date), 'yyyy年MM月dd日', { locale: ja });
};

/**
 * サブスクリプション解約パネル
 */
export function CancellationPanel({
  company,
  companyState,
  onUpdate,
}: CancellationPanelProps) {
  const { message } = App.useApp();

  // 権限チェック
  const { canCancel } = useCanCancelSubscription(company.id);

  // 最新の解約申請取得
  const { latestRequest } = useLatestCancellationRequest(company.id);

  // 解約履歴取得
  const { history } = useCancellationHistory(company.id);

  // フォーム状態
  const [cancellationForm, setCancellationForm] = useState<{
    reason: CancellationReason | null;
    reasonDetail: string;
    feedback: string;
    confirmed: boolean;
  }>({
    reason: null,
    reasonDetail: '',
    feedback: '',
    confirmed: false,
  });

  // ダイアログ表示状態
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [showUndoDialog, setShowUndoDialog] = useState(false);
  const [showReactivationDialog, setShowReactivationDialog] = useState(false);

  // Mutations
  const cancelMutation = useCancelSubscription();
  const undoMutation = useUndoCancellation();
  const reactivateMutation = trpc.subscriptionCancellation.reactivate.useMutation();

  // tRPC utils（キャッシュ無効化用）
  const utils = trpc.useUtils();

  // プラン変更予約中かどうか
  const hasScheduledPlanChange = !!(company.scheduledPlanCode && company.scheduledPlanEffectiveDate);

  // 解約可能かどうか
  const canRequestCancellation = useMemo(() => {
    if (!canCancel) return false;
    if (companyState.subscriptionStatus === 'canceling') return false;
    if (companyState.subscriptionStatus === 'canceled') return false;
    if (companyState.subscriptionStatus !== 'active') return false;
    if (hasScheduledPlanChange) return false; // プラン変更予約中は解約不可
    // 解約申請中（pending）の場合は解約不可
    if (latestRequest?.status === 'pending') return false;
    return !!(company.stripeSubscriptionId || company.bankTransferStatus === 'confirmed');
  }, [canCancel, companyState, company, hasScheduledPlanChange, latestRequest]);

  // 解約実行ハンドラ
  const handleCancellation = async () => {
    try {
      await cancelMutation.mutateAsync({
        companyId: company.id,
        reason: cancellationForm.reason || undefined,
        reasonDetail: cancellationForm.reasonDetail || undefined,
        feedback: cancellationForm.feedback || undefined,
      });

      // キャッシュを無効化
      await Promise.all([
        utils.company.getCurrentCompany.invalidate(),
        utils.subscriptionCancellation.getCompanyState.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getDeletionSchedule.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getLatestCancellationRequest.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getCancellationHistory.invalidate({ companyId: company.id }),
      ]);

      message.success('解約申請を受け付けました');
      setShowCancelDialog(false);
      setCancellationForm({
        reason: null,
        reasonDetail: '',
        feedback: '',
        confirmed: false,
      });
      onUpdate();
    } catch (error) {
      message.error('解約申請に失敗しました');
      console.error(error);
    }
  };

  // 解約取り消しハンドラ
  const handleUndoCancellation = async () => {
    try {
      const result = await undoMutation.mutateAsync({
        companyId: company.id,
      });

      // キャッシュを無効化
      await Promise.all([
        utils.company.getCurrentCompany.invalidate(),
        utils.subscriptionCancellation.getCompanyState.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getDeletionSchedule.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getLatestCancellationRequest.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getCancellationHistory.invalidate({ companyId: company.id }),
      ]);

      message.success(result.message || '解約申請を取り消しました');
      setShowUndoDialog(false);
      onUpdate();
    } catch (error) {
      // エラー時もキャッシュを無効化して最新状態を取得
      await Promise.all([
        utils.company.getCurrentCompany.invalidate(),
        utils.subscriptionCancellation.getCompanyState.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getDeletionSchedule.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getLatestCancellationRequest.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getCancellationHistory.invalidate({ companyId: company.id }),
      ]);

      const errorMessage = error instanceof Error ? error.message : '解約取り消しに失敗しました';

      // 既にアクティブの場合は特別なメッセージ
      if (errorMessage.includes('既にアクティブ')) {
        message.info('サブスクリプションは既にアクティブです。ページを更新します。');
        onUpdate();
      } else {
        message.error(errorMessage);
      }

      setShowUndoDialog(false);
      console.error(error);
    }
  };

  // 復活処理ハンドラ（プレビューダイアログからの確定）
  const handleReactivation = async (planCode: string, addOnIndices: number[]) => {
    try {
      const result = await reactivateMutation.mutateAsync({
        companyId: company.id,
        planCode,
        addOnIndices,
      });

      // キャッシュを無効化
      await Promise.all([
        utils.company.getCurrentCompany.invalidate(),
        utils.subscriptionCancellation.getCompanyState.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getDeletionSchedule.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getLatestCancellationRequest.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getCancellationHistory.invalidate({ companyId: company.id }),
      ]);

      message.success(result.message || 'サブスクリプションを復活しました');
      setShowReactivationDialog(false);
      onUpdate();
    } catch (error) {
      // エラー時もキャッシュを無効化して最新状態を取得
      await Promise.all([
        utils.company.getCurrentCompany.invalidate(),
        utils.subscriptionCancellation.getCompanyState.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getDeletionSchedule.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getLatestCancellationRequest.invalidate({ companyId: company.id }),
        utils.subscriptionCancellation.getCancellationHistory.invalidate({ companyId: company.id }),
      ]);

      const errorMessage = error instanceof Error ? error.message : '復活に失敗しました';
      message.error(errorMessage);
      console.error(error);
    }
  };

  // 解約済み（canceled）ステータスかどうか
  const isCanceled = companyState.subscriptionStatus === 'canceled';
  // 解約申請中（canceling または pending request）かどうか
  const isCancelingOrPending = companyState.subscriptionStatus === 'canceling' || latestRequest?.status === 'pending';

  return (
    <div className="cancellation-panel">
      {/* 解約済み（canceled）の復活UI */}
      {isCanceled && (
        <Card
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
              サブスクリプションが解約済みです
            </Space>
          }
          style={{ marginBottom: 24, borderColor: '#ff4d4f' }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="error"
              message="サブスクリプションは解約済みです"
              description={
                <>
                  契約が終了しています。
                  {company.subscriptionCanceledAt && (
                    <>
                      <br />
                      解約日: <strong>{formatDate(company.subscriptionCanceledAt)}</strong>
                    </>
                  )}
                </>
              }
              showIcon
            />

            {canCancel && (
              <>
                <Paragraph>
                  サブスクリプションを復活する場合は、以下のボタンをクリックしてください。
                  料金の見積もりとプラン・追加オプションの選択ができます。
                </Paragraph>
                <Button
                  type="primary"
                  onClick={() => setShowReactivationDialog(true)}
                  loading={reactivateMutation.isPending}
                >
                  サブスクリプションを復活する
                </Button>
              </>
            )}

            {!canCancel && (
              <Alert
                type="info"
                message="サブスクリプションの復活には管理者権限が必要です"
                showIcon
              />
            )}
          </Space>
        </Card>
      )}

      {/* 解約申請中の警告と取り消しUI */}
      {isCancelingOrPending && !isCanceled && (
        <Card
          title={
            <Space>
              <ExclamationCircleOutlined style={{ color: '#faad14' }} />
              解約申請中
            </Space>
          }
          style={{ marginBottom: 24, borderColor: '#faad14' }}
        >
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <Alert
              type="warning"
              message="解約が予定されています"
              description={
                <>
                  <strong>{formatDate(latestRequest?.effectiveDate)}</strong> に解約が有効になります。
                  <br />
                  契約終了まで引き続きサービスをご利用いただけます。
                  {latestRequest?.reason && (
                    <>
                      <br />
                      <br />
                      解約理由: {CANCELLATION_REASON_LABELS[latestRequest.reason]}
                    </>
                  )}
                </>
              }
              showIcon
            />

            {canCancel && (
              <>
                <Paragraph>
                  解約を取り消す場合は、以下のボタンをクリックしてください。
                </Paragraph>
                <Button
                  type="primary"
                  onClick={() => setShowUndoDialog(true)}
                  loading={undoMutation.isPending}
                >
                  解約を取り消す
                </Button>
              </>
            )}

            {!canCancel && (
              <Alert
                type="info"
                message="解約の取り消しには管理者権限が必要です"
                showIcon
              />
            )}
          </Space>
        </Card>
      )}

      {/* 解約履歴表示（オプション） */}
      {history && history.length > 0 && (
        <Collapse
          style={{ marginBottom: 24 }}
          items={[
            {
              key: 'history',
              label: (
                <Space>
                  <HistoryOutlined />
                  過去の解約履歴 ({history.length}件)
                </Space>
              ),
              children: (
                <Timeline
                  items={history.map((req) => ({
                    key: req.id,
                    color:
                      req.status === 'effective' ? 'red' :
                      req.status === 'undone' ? 'green' :
                      'blue',
                    children: (
                      <Space direction="vertical" size="small">
                        <Text strong>
                          {formatDate(req.requestedAt)}
                        </Text>
                        <Text>
                          ステータス: {CANCELLATION_REQUEST_STATUS_LABELS[req.status]}
                        </Text>
                        {req.reason && (
                          <Text>
                            理由: {CANCELLATION_REASON_LABELS[req.reason]}
                          </Text>
                        )}
                        {req.undoneAt && (
                          <Text type="secondary">
                            取り消し日時: {formatDate(req.undoneAt)}
                          </Text>
                        )}
                      </Space>
                    ),
                  }))}
                />
              ),
            },
          ]}
        />
      )}

      {/* プラン変更予約中の場合 */}
      {hasScheduledPlanChange && !isCanceled && !isCancelingOrPending && (
        <Alert
          type="info"
          message="プラン変更予約中のため解約できません"
          description={
            <>
              現在、<strong>{formatDate(company.scheduledPlanEffectiveDate)}</strong> からのプラン変更が予約されています。
              <br />
              解約をご希望の場合は、プラン変更が適用された後に再度お申し込みください。
              <br />
              <br />
              プラン変更をキャンセルしたい場合は、「基本契約」タブからプランを変更してください。
            </>
          }
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 解約不可の場合（canceled や suspended/pending 以外の場合のみ表示） */}
      {!canRequestCancellation && !hasScheduledPlanChange && !isCanceled && !isCancelingOrPending && (
        <Alert
          type="info"
          message="解約できません"
          description={
            !canCancel
              ? '解約権限がありません。会社の管理者にお問い合わせください。'
              : '現在、解約可能な契約がありません。'
          }
          showIcon
          style={{ marginBottom: 24 }}
        />
      )}

      {/* 解約説明セクション（解約可能・解約申請中どちらでも表示） */}
      {(canRequestCancellation || latestRequest?.status === 'pending') && (
        <Card title="解約について" style={{ marginBottom: 24 }}>
          <Space direction="vertical" size="middle" style={{ width: '100%' }}>
            <div>
              <Title level={5}>解約のタイミング</Title>
              <Paragraph>
                解約をお申し込みいただくと、<strong>現在の契約期間終了日</strong>
                をもってサービスが停止されます。契約期間中は引き続きサービスをご利用いただけます。
              </Paragraph>
              <Paragraph type="secondary">
                契約終了日: {formatDate(latestRequest?.effectiveDate || company.subscriptionEndsAt)}
              </Paragraph>
            </div>

            <div>
              <Title level={5}>ご注意事項</Title>
              <ul>
                <li>契約終了後、ログインできなくなります</li>
                <li>契約終了後、データは削除されます</li>
                <li>削除されたデータは復元できません</li>
              </ul>
            </div>
          </Space>
        </Card>
      )}

      {/* 解約可能な場合のUI（解約フォーム） */}
      {canRequestCancellation && (
        <>
          {/* 解約理由アンケート */}
          <Card title="解約理由をお聞かせください" style={{ marginBottom: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Text>サービス改善のため、解約理由をお聞かせください（任意）</Text>
                <Radio.Group
                  value={cancellationForm.reason}
                  onChange={(e) => setCancellationForm({
                    ...cancellationForm,
                    reason: e.target.value
                  })}
                  style={{ marginTop: 12 }}
                >
                  <Space direction="vertical">
                    {Object.entries(CANCELLATION_REASON_LABELS).map(([key, label]) => (
                      <Radio key={key} value={key}>
                        {label}
                      </Radio>
                    ))}
                  </Space>
                </Radio.Group>
              </div>

              {/* その他を選択した場合の詳細入力 */}
              {cancellationForm.reason === 'other' && (
                <div>
                  <Text>詳細をお聞かせください</Text>
                  <Input.TextArea
                    value={cancellationForm.reasonDetail}
                    onChange={(e) => setCancellationForm({
                      ...cancellationForm,
                      reasonDetail: e.target.value
                    })}
                    rows={3}
                    maxLength={500}
                    placeholder="解約理由の詳細をご記入ください"
                    showCount
                    style={{ marginTop: 8 }}
                  />
                </div>
              )}

              {/* 自由記述フィードバック */}
              <div>
                <Text>
                  その他、ご意見・ご要望がございましたらお聞かせください（任意）
                </Text>
                <Input.TextArea
                  value={cancellationForm.feedback}
                  onChange={(e) => setCancellationForm({
                    ...cancellationForm,
                    feedback: e.target.value
                  })}
                  rows={4}
                  maxLength={1000}
                  placeholder="サービスに関するご意見・ご要望をご自由にお書きください"
                  showCount
                  style={{ marginTop: 8 }}
                />
              </div>
            </Space>
          </Card>

          {/* 3. 解約実行セクション */}
          <Card>
            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
              <Checkbox
                checked={cancellationForm.confirmed}
                onChange={(e) => setCancellationForm({
                  ...cancellationForm,
                  confirmed: e.target.checked
                })}
              >
                上記の内容を理解し、解約を申し込みます
              </Checkbox>

              <Button
                type="primary"
                danger
                size="large"
                disabled={!cancellationForm.confirmed}
                onClick={() => setShowCancelDialog(true)}
                loading={cancelMutation.isPending}
              >
                解約を申し込む
              </Button>
            </Space>
          </Card>
        </>
      )}

      {/* 解約確認ダイアログ */}
      <CancellationConfirmDialog
        open={showCancelDialog}
        company={company}
        onConfirm={handleCancellation}
        onCancel={() => setShowCancelDialog(false)}
        loading={cancelMutation.isPending}
      />

      {/* 解約取り消し確認ダイアログ（解約申請中の取り消し用） */}
      <UndoCancellationConfirmDialog
        open={showUndoDialog}
        cancelEffectiveDate={latestRequest?.effectiveDate}
        isRestoreFromCanceled={false}
        onConfirm={handleUndoCancellation}
        onCancel={() => setShowUndoDialog(false)}
        loading={undoMutation.isPending}
      />

      {/* 復活プレビューダイアログ（解約済みからの復活用） */}
      <ReactivationPreviewDialog
        open={showReactivationDialog}
        company={company}
        onConfirm={handleReactivation}
        onCancel={() => setShowReactivationDialog(false)}
        loading={reactivateMutation.isPending}
      />
    </div>
  );
}
